/**
 * Rekenwerk achter het scherm Prijsgrafiek (US-023, US-022).
 *
 * Staat hier en niet in het scherm omdat de laagste marktprijs per dag op twee plaatsen nodig is:
 * de API rekent hem uit voor de kaartjes van alle producten, en het scherm voor de grafiek en de
 * kengetallen van het geopende product. Dat twee keer uitschrijven is precies hoe de eigen-prijsregel
 * eerder op vier plekken uit elkaar is gelopen.
 *
 * Alles hier rekent met wat er gemeten ís. Een dag zonder geldige meting is niet nul en niet
 * "gelijk gebleven" — hij is onbekend, en wordt overgeslagen. Dat is dezelfde lijn als het
 * lijndiagram, dat bij een gat de lijn onderbreekt in plaats van hem door te trekken.
 */
/**
 * De laagste marktprijs per dag: het minimum over de concurrenten.
 *
 * De eigen bron doet niet mee — dit is bedoeld om ónze prijs tegen de markt te zetten, en dan mag
 * hij niet zijn eigen vergelijkingswaarde bepalen.
 */
export function laagsteMarktprijsPerDag(reeksen) {
    const concurrenten = reeksen.filter((r) => !r.isOwn);
    const aantalDagen = concurrenten[0]?.waarden.length ?? 0;
    return Array.from({ length: aantalDagen }, (_, dag) => {
        const gemeten = concurrenten
            .map((reeks) => reeks.waarden[dag] ?? null)
            .filter((waarde) => waarde !== null);
        return gemeten.length > 0 ? Math.min(...gemeten) : null;
    });
}
/** De laatste dag waarop iets is gemeten, met zijn waarde. `null` als de reeks leeg is. */
export function laatsteWaarde(waarden) {
    for (let dag = waarden.length - 1; dag >= 0; dag -= 1) {
        const waarde = waarden[dag];
        if (waarde !== null && waarde !== undefined)
            return { dag, waarde };
    }
    return null;
}
/** De eerste dag waarop iets is gemeten, met zijn waarde. */
export function eersteWaarde(waarden) {
    for (let dag = 0; dag < waarden.length; dag += 1) {
        const waarde = waarden[dag];
        if (waarde !== null && waarde !== undefined)
            return { dag, waarde };
    }
    return null;
}
/**
 * Aantal dagen waarop een concurrent een andere prijs had dan de vorige gemeten dag.
 *
 * Vergelijkt met de laatste dag waarop díe bron een prijs had, niet met de kalenderdag ervoor. Bij
 * een mislukte meting tussendoor zou dat anders een wijziging opleveren die er geen is: er is niets
 * veranderd, er is een dag niet gemeten.
 */
export function dagenMetWijziging(reeksen) {
    const concurrenten = reeksen.filter((r) => !r.isOwn);
    const aantalDagen = concurrenten[0]?.waarden.length ?? 0;
    const vorige = new Map();
    let dagen = 0;
    for (let dag = 0; dag < aantalDagen; dag += 1) {
        let gewijzigd = false;
        for (const reeks of concurrenten) {
            const waarde = reeks.waarden[dag];
            if (waarde === null || waarde === undefined)
                continue;
            const eerder = vorige.get(reeks.sourceId);
            if (eerder !== undefined && eerder !== waarde)
                gewijzigd = true;
            vorige.set(reeks.sourceId, waarde);
        }
        if (gewijzigd)
            dagen += 1;
    }
    return dagen;
}
/**
 * De concurrent die over de periode het meest is bewogen, op- of neerwaarts.
 *
 * Op procenten en niet op centen, zodat een duur product een goedkoop product niet altijd overstemt.
 */
export function grootsteBeweging(reeksen) {
    let sterkste = null;
    for (const reeks of reeksen.filter((r) => !r.isOwn)) {
        const eerste = eersteWaarde(reeks.waarden);
        const laatste = laatsteWaarde(reeks.waarden);
        // Eén enkele meting is geen beweging: er is niets om hem mee te vergelijken.
        if (!eerste || !laatste || eerste.dag === laatste.dag || eerste.waarde === 0)
            continue;
        const procent = ((laatste.waarde - eerste.waarde) / eerste.waarde) * 100;
        if (!sterkste || Math.abs(procent) > Math.abs(sterkste.procent)) {
            sterkste = { name: reeks.name, procent };
        }
    }
    return sterkste;
}
/**
 * Een reeks omgerekend naar de afwijking in procenten ten opzichte van de eigen prijs van díe dag.
 *
 * Per dag en niet tegen één vast ijkpunt: als onze eigen prijs halverwege de periode wijzigt,
 * verschuiven alle afstanden mee. Dat is juist wat je wilt zien — de vraag is "hoe ver zit deze bron
 * van ons af", elke dag opnieuw.
 */
export function afwijkingTovEigen(waarden, eigen) {
    return waarden.map((waarde, dag) => {
        const basis = eigen[dag] ?? null;
        if (waarde === null || basis === null || basis === 0)
            return null;
        return ((waarde - basis) / basis) * 100;
    });
}
//# sourceMappingURL=grafiek.js.map